import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    OnInit,
    Output,
    ChangeDetectionStrategy
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbar } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { combineLatest, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import {
    ClientService,
    LanguagesService,
    ProviderLinkService,
    SwitchLanguageService
} from 'src/app/core/services';
import { AuthPopupService, AuthService } from 'src/app/features/auth/services';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        TranslocoModule,
        MatMenuModule,
        MatIcon,
        FlexLayoutModule,
        MatToolbar,
        MatButtonModule
    ]
})
export class HeaderComponent implements OnInit {
    @Output() public sidenavToggle = new EventEmitter();

    languages = [
        { code: 'en', name: 'English' },
        { code: 'es', name: 'Español' }
    ];
    get selectedLanguage(): string | null {
        return sessionStorage.getItem('selectedLanguage');
    }

    get selectedLanguageName(): string {
        return this.selectedLanguage === 'es' ? 'Español' : 'English';
    }

    user$ = this._auth.user$;

    clientLogoPath: string | undefined;

    clientIsEnabled: boolean = false;

    constructor(
        private service: TranslocoService,
        public router: Router,
        private readonly _auth: AuthService,
        private readonly _authPopup: AuthPopupService,
        private readonly _client: ClientService,
        private readonly _switchLanguage: SwitchLanguageService,
        private readonly _language: LanguagesService,
        private readonly _providerLink: ProviderLinkService
    ) {}

    get isSignedIn(): boolean {
        return this._auth.isSignedIn;
    }

    ngOnInit() {
        this._auth.user$.subscribe((user) => {
            let selectedLanguage = undefined;
            if (user?.preferredLanguage?.startsWith('en')) {
                selectedLanguage = 'en';
            } else if (user?.preferredLanguage?.startsWith('es')) {
                selectedLanguage = 'es';
            } else if (this.selectedLanguage === null) {
                selectedLanguage = 'en';
            } else {
                selectedLanguage = this.selectedLanguage;
            }
            sessionStorage.setItem(
                'selectedLanguage',
                selectedLanguage ?? 'en'
            );
            if (this.service.getActiveLang() !== this.selectedLanguage)
                this.service.setActiveLang(this.selectedLanguage!);
        });
        this._client.latest.subscribe((client) => {
            this.clientIsEnabled = client?.isEnabled == true;
            this.clientLogoPath = client?.logoPath
                ? environment.apiUrl + client.logoPath
                : undefined;
        });
    }

    public onToggleSidenav = () => {
        this.sidenavToggle.emit();
    };

    public selectLanguage(val: string) {
        if (this.isSignedIn) {
            combineLatest([
                this._auth.user$.pipe(take(1)),
                this._language.languages$.pipe(take(1))
            ])
                .pipe(
                    switchMap(([user, languages]) => {
                        const language = languages.find((l) =>
                            l.languageCode.trim().includes(val)
                        );
                        if (
                            user &&
                            language &&
                            user.preferredLanguageId !== language.languageId
                        ) {
                            user.preferredLanguageId = language.languageId;
                            return this._auth.updateUser(user);
                        }
                        // Nothing to persist (the preference already matches, or the
                        // language/user is missing), but the switch itself must still
                        // happen — returning EMPTY here would complete without emitting
                        // and the language would silently never change.
                        return of(null);
                    })
                )
                .subscribe(() => this._switchLanguage.switch(val));
        } else {
            this._switchLanguage.switch(val);
        }
    }

    signIn() {
        this._authPopup.showSignIn();
    }

    signOut() {
        this.router.navigate(['/sign-out']);
    }
    navigation(route: string) {
        if (route === 'home' && this._providerLink.hasProvider)
            this.router.navigateByUrl(
                `${runtimeConfig.clientCode}/providers/${this._providerLink.providerId}`
            );
        else this.router.navigateByUrl(`${runtimeConfig.clientCode}/${route}`);
    }
}
